'use client';
import * as React from 'react';
import Link from 'next/link';

import { Store } from '@/lib/types/customer/restaurant/StoreProfile';
import useRoutePath from '@/hooks/useRoutePath';

import { StoreOpeningHours } from './StoreOpeningHours';
import { Location } from '@/components/ui/Icons';
import SvgClock from '@/components/ui/Icons/SvgClock';
import SvgCart from '@/components/ui/Icons/Cart';
import SvgGlobe from '@/components/ui/Icons/Globe';
import SvgDeliveryMini from '@ui/Icons/DeliveryMini';
import RestaurantLocationMap from './RestaurantLocationMap';
import { cn } from '@/lib/utils';
import {
  formatTime,
  isPaidSubscriber,
  showAddress,
} from '@/lib/helpers/functions';
import {
  FacebookBW,
  InstagramBW,
  LinkedinBW,
  TwitterBW,
  WhatsappBW,
  YoutubeBW,
} from '@/components/ui/Icons/SocialIconsBW';
import { RestaurantConfig } from '@/lib/types/customer/restaurant/RestaurantConfig';
import { useTranslations } from 'next-intl';
import { PhoneIcon } from '@heroicons/react/24/outline';
import If from '@ui/If';

const socials = [
  {
    key: 'facebook',
    icon: <FacebookBW />,
  },
  {
    key: 'twitter',
    icon: <TwitterBW />,
  },
  {
    key: 'instagram',
    icon: <InstagramBW />,
  },
  {
    key: 'whatsapp',
    icon: <WhatsappBW />,
  },
  {
    key: 'linkedin',
    icon: <LinkedinBW />,
  },
  {
    key: 'youtube',
    icon: <YoutubeBW />,
  },
];

const infoIcons = {
  location: <Location />,
  clock: <SvgClock />,
  cart: <SvgCart />,
  delivery: <SvgDeliveryMini />,
  globe: <SvgGlobe />,
  phone: <PhoneIcon className="ml-1 h-5 w-5" />,
};

const cleanLink = (url: string): string => {
  let cleanedUrl = url.replace(/^(https?):\/\//, '');
  if (cleanedUrl.startsWith('www.')) {
    cleanedUrl = cleanedUrl.slice(4);
  }
  return cleanedUrl;
};

const formatDeliveryTime = (storeInfo: RestaurantConfig) =>
  `${storeInfo.minimum_delivery_time_in_minutes}-${storeInfo.maximum_delivery_time_in_minutes} min`;

const formatMinOrderValue = (storeInfo: RestaurantConfig) =>
  `min. ${storeInfo.formatted_currency?.minimum_order_value_for_delivery}`;

const formatDeliveryFee = (storeInfo: RestaurantConfig, free: string) =>
  storeInfo.delivery_fee === 0
    ? free
    : storeInfo.formatted_currency?.delivery_fee;

const formatWebsite = (storeInfo: RestaurantConfig) => {
  const url = cleanLink(storeInfo?.links?.website ?? '');
  return (
    <a
      target="_blank"
      href={storeInfo?.links?.website}
      rel="noopener noreferrer"
      className="hover:underline hover:underline-offset-4"
    >
      {url}
    </a>
  );
};

const formatOpenUntil = (store: Store, localisedString: string) =>
  store.is_open_until
    ? localisedString + ` ${formatTime(store.is_open_until)}`
    : '';

interface RestaurantInfoProps {
  store: Store;
  id: string;
}

export default function RestaurantInfo({ store, id }: RestaurantInfoProps) {
  const TSTORE_PROFILE = useTranslations('storefront.store_profile');
  const TCOMMON = useTranslations();
  const { pathname } = useRoutePath();
  const { restaurant_config: storeInfo } = store;

  const { links } = storeInfo;
  let updatedLinks;
  if (links) {
    updatedLinks = socials
      .filter(
        (social) =>
          links[social.key] !== null &&
          links[social.key] !== undefined &&
          links[social.key] !== ''
      )
      .map((social) => ({
        name: social.key,
        icon: social.icon,
        link: links[social.key],
      }));
  }

  const restaurantAllowsDelivery =
    (store?.allows_delivery && store?.features?.handle_orders) || false;

  return (
    <div className="space-y-5 rounded bg-white p-5 shadow-lg lg:max-w-[324px]">
      <StoreOpeningHours
        shifts={store.restaurant_shifts || []}
        activeSubscription={store.active_subscription}
        isOpen={store.is_open}
        isOpeningSoon={store.is_opening_soon}
      />
      {store.is_open && store.features.handle_orders && (
        <div className="flex items-center space-x-5">
          <Link
            href={`${pathname}/menus`}
            prefetch={false}
            className={cn(
              'w-auto rounded-full bg-gradient-to-r from-[#F5375C] to-[#C33076] px-[25px] py-[15px] text-white hover:shadow-md hover:shadow-red-300'
            )}
          >
            {TSTORE_PROFILE('order_now')}
          </Link>
        </div>
      )}
      <RestaurantLocationMap
        lat={store.lat}
        lng={store.lng}
        name={store.name}
        street={`${store.address.street_name} ${store.address.house_number}, ${store.address.zip_code}`}
        city={store.address.city}
        id={id}
      />
      <div className="space-y-3">
        <h3 className="text-h5 font-bold">{TSTORE_PROFILE('business_info')}</h3>
        <ul className="space-y-2 text-sm leading-[130%] text-[#646464]">
          <If condition={store.address}>
            <ListInfo
              item={{
                icon: infoIcons.location,
                value: showAddress(store.address),
              }}
            />
          </If>
          <If condition={restaurantAllowsDelivery}>
            <If
              condition={
                storeInfo.minimum_delivery_time_in_minutes &&
                storeInfo.minimum_delivery_time_in_minutes > 0
              }
              fallback={<NotAvailable store={store} icon={infoIcons.clock} />}
            >
              <ListInfo
                item={{
                  icon: infoIcons.clock,
                  value: formatDeliveryTime(storeInfo),
                }}
              />
            </If>
            <If
              condition={
                storeInfo.minimum_order_value_for_delivery &&
                storeInfo.minimum_order_value_for_delivery > 0
              }
              fallback={<NotAvailable store={store} icon={infoIcons.cart} />}
            >
              <ListInfo
                item={{
                  icon: infoIcons.cart,
                  value: formatMinOrderValue(storeInfo),
                }}
              />
            </If>
            <If
              condition={storeInfo.delivery_fee !== undefined}
              fallback={
                <NotAvailable store={store} icon={infoIcons.delivery} />
              }
            >
              <ListInfo
                item={{
                  icon: infoIcons.delivery,
                  value: formatDeliveryFee(storeInfo, TCOMMON('free')),
                }}
              />
            </If>
          </If>
          <If
            condition={store.is_open_until}
            fallback={
              <NotAvailable
                store={store}
                icon={infoIcons.clock}
                isTimeRelated={true}
              />
            }
          >
            <ListInfo
              item={{
                icon: infoIcons.clock,
                value: formatOpenUntil(store, TSTORE_PROFILE('open_until')),
              }}
            />
          </If>
          <If
            condition={storeInfo.links?.website}
            fallback={<NotAvailable store={store} icon={infoIcons.globe} />}
          >
            <ListInfo
              item={{
                icon: infoIcons.globe,
                value: formatWebsite(storeInfo),
              }}
            />
          </If>
          <If
            condition={store.phone}
            fallback={<NotAvailable store={store} icon={infoIcons.phone} />}
          >
            <ListInfo
              item={{
                icon: infoIcons.phone,
                value: store.phone,
              }}
            />
          </If>
        </ul>
        <ul className="flex items-center space-x-2 pt-4">
          {updatedLinks?.map((item) => {
            return (
              <Link
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full bg-white shadow-lg hover:text-white hover:drop-shadow-2xl"
                key={item.name}
                href={item.link as string}
                aria-label={TCOMMON('aria.navigate_to_link', {
                  name: item.name,
                })}
              >
                {item.icon}
              </Link>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function ListInfo({
  item,
}: {
  item: { icon: React.JSX.Element; value: string | React.JSX.Element };
}) {
  return (
    <li className="flex items-center space-x-2">
      {item.icon}
      <div>{item.value}</div>
    </li>
  );
}

function NotAvailable({
  store,
  icon,
  isTimeRelated = false,
}: {
  store: Store;
  icon: React.JSX.Element;
  isTimeRelated?: boolean;
}) {
  const t = useTranslations();
  const subscriberIsPaid = isPaidSubscriber(store.active_subscription);
  if (subscriberIsPaid) return <></>;
  else
    return (
      <li className="flex items-center space-x-2">
        {icon}
        <div>
          <If
            condition={!isTimeRelated}
            fallback={t('storefront.store_profile.currently_closed')}
          >
            {t('common.not_available')}
          </If>
        </div>
      </li>
    );
}
